import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import { fmtBytes } from "@/lib/webApi";

export function Card({ className, children, ...rest }) {
  return <div className={cn("rounded-[--w-radius] border border-[--w-border] bg-[--w-card]", className)} {...rest}>{children}</div>;
}

export function Eyebrow({ className, children, ...rest }) {
  return <p className={cn("text-[11px] font-semibold uppercase tracking-[0.08em] text-[--w-text-3]", className)} {...rest}>{children}</p>;
}

const TOP = { info: "border-t-[--w-info]", warn: "border-t-[--w-warn]", ok: "border-t-[--w-ok]", danger: "border-t-[--w-danger]", neutral: "border-t-[--w-border-2]" };

export function KpiCard({ eyebrow, value, sub, tone = "neutral", className, ...rest }) {
  return (
    <Card className={cn("border-t-[3px] p-5", TOP[tone], className)} {...rest}>
      <Eyebrow>{eyebrow}</Eyebrow>
      <p className="mt-2 text-4xl font-semibold tracking-tight tabular-nums text-[--w-text]">{value}</p>
      <p className="mt-1 text-sm text-[--w-text-2]">{sub}</p>
    </Card>
  );
}

export function Section({ title, action, className, children, ...rest }) {
  return (
    <section className={className} {...rest}>
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-lg font-semibold text-[--w-text]">{title}</h2>
        {action}
      </div>
      <div className="mt-3">{children}</div>
    </section>
  );
}

export function Meter({ label, used, cap, className, ...rest }) {
  const pct = cap ? Math.min(100, ((used || 0) / cap) * 100) : 0;
  return (
    <div className={className} {...rest}>
      <div className="mb-1.5 flex items-center justify-between gap-3">
        <span className="text-sm text-[--w-text-2]">{label}</span>
        <span className="text-xs tabular-nums text-[--w-text-3]">{fmtBytes(used)} {cap ? `of ${fmtBytes(cap)}` : "used · unlimited"}</span>
      </div>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-stone-100"><div className="h-full rounded-full bg-[--w-accent] transition-[width] duration-500" style={{ width: `${pct}%` }} /></div>
    </div>
  );
}

const PILL = { ok: "bg-emerald-50 text-emerald-700", warn: "bg-amber-50 text-amber-700", danger: "bg-red-50 text-red-700", info: "bg-blue-50 text-blue-700", neutral: "bg-stone-100 text-[--w-text-2]" };

export function Pill({ tone = "neutral", className, children, ...rest }) {
  return <span className={cn("inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium", PILL[tone], className)} {...rest}>{children}</span>;
}

const BTN = {
  primary: "bg-[--w-brand] text-white hover:bg-stone-800",
  ghost: "border border-[--w-border] bg-[--w-card] text-[--w-text-2] hover:bg-[--w-card-2] hover:text-[--w-text]",
  danger: "border border-red-200 text-red-700 hover:bg-red-50",
};

export function Btn({ variant = "ghost", className, ...rest }) {
  return <button className={cn("inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors duration-200", BTN[variant], className)} {...rest} />;
}

export function EmptyState({ icon: Icon, className, children, ...rest }) {
  return (
    <div className={cn("rounded-[--w-radius] border border-dashed border-[--w-border-2] bg-[--w-card] p-8 text-center text-sm text-[--w-text-2]", className)} {...rest}>
      {Icon && <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-stone-100 text-[--w-text-3]"><Icon className="h-5 w-5" /></div>}
      {children}
    </div>
  );
}

export function ListRow({ to, className, children, ...rest }) {
  const cls = cn("flex items-center justify-between gap-3 px-4 py-3 text-sm transition-colors duration-200 hover:bg-[--w-card-2]", className);
  return to ? <Link to={to} className={cls} {...rest}>{children}</Link> : <div className={cls} {...rest}>{children}</div>;
}
